import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Botao,
  EmptyState,
  ErrorState,
  Field,
  Input,
  ListaSelecao,
  Modal,
  Pagination,
  Skeleton,
} from '../../../ui';
import ResumoRegistro from '../../ti/ResumoRegistro';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { extrairMensagemErro } from '../../../utils/apiError';
import { searchAssociadosByNome } from '../../../api/associado/associadoApi';
import type { SearchAssociadosResponse } from '../../../api/associado/associadoApi';
import { enviarAssociadoParaSgaPorCpfCnpj } from '../../../api/sga-associado/sga-associadoApi';

/**
 * Envio manual de um associado ao SGA (tela `/ti`).
 * Reescrito sobre o design system na at-67 (frente D/D5).
 *
 * 🔴 **O que mudou:**
 *
 *   1. **A paginação deixou de ser calculada na tela.** Havia um
 *      `Math.ceil(total/limit)` e — pior — um `useMemo` inteiro montando uma lista
 *      de páginas com reticências (`1 … 4 5 6 … 20`) que **nunca era renderizada**:
 *      código morto de 20 linhas. Agora é o `Pagination` do kit, que recebe
 *      `totalItens`/`porPagina` justamente para ninguém reintroduzir o
 *      "Página 1 de 0" (at-56).
 *   2. **A lista virou `ListaSelecao`.** Era `<div role="button" tabIndex={0}>`
 *      que respondia só a **Enter** — não a Espaço, como todo botão de verdade.
 *   3. **`FullScreenLoader` dentro do modal** virou `Skeleton` no corpo.
 *   4. **Falha na busca deixou de ser só um toast**: vira `ErrorState`, e "nenhum
 *      resultado" continua tendo texto próprio (`EmptyState`) — são coisas
 *      diferentes.
 *
 * ⚠️ **A busca continua EXPLÍCITA** (campo + botão "Buscar"), e não um
 * `SearchInput` com debounce: é uma consulta server-side por nome, paginada, e o
 * hábito do TI (e o desenho do backend) é "digita o nome e procura". Debounce aqui
 * dispararia uma consulta a cada pausa da digitação.
 *
 * ⚠️ **A chamada ao SGA não mudou**: `enviarAssociadoParaSgaPorCpfCnpj(cpf)`, e o
 * modal fecha no sucesso.
 */

const POR_PAGINA = 10;

export type AssociadoSearchItem = {
  id: string;
  nomeCompleto: string | null;
  cpfCnpj: string | null;
  email: string | null;
  codigoAssociado?: string | null;
  regional?: string | null;
  createdAt?: string;
};

interface ModalEnvioDeAssociadoProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmEnviar?: (associado: AssociadoSearchItem) => Promise<void> | void;
}

const ModalEnvioDeAssociado: React.FC<ModalEnvioDeAssociadoProps> = ({
  isOpen,
  onClose,
  onConfirmEnviar,
}) => {
  const [termo, setTermo] = useState('');
  /** O que está efetivamente buscado (só muda ao clicar em "Buscar"). */
  const [consulta, setConsulta] = useState('');
  const [pagina, setPagina] = useState(1);
  /**
   * Contador de tentativas — é o que faz "Buscar" de novo com o MESMO termo (e o
   * "Tentar novamente" do erro) refazer a consulta. Sem ele, `setConsulta` com o
   * valor igual não muda o estado e o efeito não roda.
   */
  const [tentativa, setTentativa] = useState(0);

  const [associados, setAssociados] = useState<AssociadoSearchItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selecionado, setSelecionado] = useState<AssociadoSearchItem | null>(null);

  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTermo('');
    setConsulta('');
    setPagina(1);
    setTentativa(0);
    setAssociados([]);
    setTotal(0);
    setSelecionado(null);
    setBuscando(false);
    setErroBusca('');
    setEnviando(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !consulta) return undefined;

    let vivo = true;
    setBuscando(true);
    setErroBusca('');

    (async () => {
      try {
        const resposta: SearchAssociadosResponse = await searchAssociadosByNome(
          consulta,
          pagina,
          POR_PAGINA,
        );
        if (!vivo) return;

        setAssociados(Array.isArray(resposta?.items) ? (resposta.items as AssociadoSearchItem[]) : []);
        setTotal(Number(resposta?.total) || 0);
      } catch (falha) {
        if (!vivo) return;
        setErroBusca(extrairMensagemErro(falha, 'Não foi possível buscar os associados.'));
        setAssociados([]);
        setTotal(0);
      } finally {
        if (vivo) setBuscando(false);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [isOpen, consulta, pagina, tentativa]);

  const buscar = () => {
    const q = termo.trim();
    if (!q) return;
    setPagina(1);
    setConsulta(q);
    setTentativa((atual) => atual + 1);
  };

  const confirmarEnvio = async () => {
    if (!selecionado) return;

    const documento = (selecionado.cpfCnpj ?? '').trim();
    if (!documento) {
      toast.error('Associado selecionado não possui CPF/CNPJ.');
      return;
    }

    setEnviando(true);
    try {
      if (onConfirmEnviar) {
        await onConfirmEnviar(selecionado);
      } else {
        await enviarAssociadoParaSgaPorCpfCnpj(documento);
      }

      toast.success('Envio solicitado com sucesso');
      onClose();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao enviar associado'));
    } finally {
      setEnviando(false);
    }
  };

  const mostrarPaginacao = !selecionado && !!consulta && !buscando && !erroBusca && total > 0;

  return (
    <Modal
      aberto={isOpen}
      titulo="Envio de Associado"
      descricao="Envia ao SGA um associado já cadastrado no SAAC."
      tamanho="lg"
      ocupado={enviando}
      aoFechar={onClose}
      rodape={
        selecionado ? (
          <>
            <Botao variante="secundario" onClick={onClose} disabled={enviando}>
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              carregando={enviando}
              disabled={!(selecionado.cpfCnpj ?? '').trim()}
              onClick={confirmarEnvio}
            >
              Confirmar envio
            </Botao>
          </>
        ) : mostrarPaginacao ? (
          <Pagination
            pagina={pagina}
            totalItens={total}
            porPagina={POR_PAGINA}
            aoTrocar={setPagina}
            ocupado={buscando}
          />
        ) : (
          <Botao variante="secundario" onClick={onClose} disabled={buscando}>
            Fechar
          </Botao>
        )
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {!selecionado && (
          <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-end">
            <Field rotulo="Nome do associado" className="sm:tw-flex-1">
              <Input
                value={termo}
                placeholder="Nome completo"
                disabled={buscando}
                onChange={(evento) => setTermo(evento.target.value)}
                onKeyDown={(evento) => {
                  if (evento.key === 'Enter') buscar();
                }}
              />
            </Field>
            <Botao
              variante="primario"
              iconeEsquerda="busca"
              carregando={buscando}
              disabled={!termo.trim()}
              onClick={buscar}
            >
              Buscar
            </Botao>
          </div>
        )}

        {!selecionado && buscando && <Skeleton linhas={3} altura="tw-h-14" />}

        {!selecionado && !buscando && erroBusca && (
          <ErrorState
            mensagem="Não foi possível buscar os associados."
            detalhe={erroBusca}
            aoTentarNovamente={() => setTentativa((atual) => atual + 1)}
          />
        )}

        {!selecionado && !buscando && !erroBusca && !!consulta && (
          <ListaSelecao
            itens={associados}
            chave={(item) => String(item.id)}
            rotulo={(item) => item.nomeCompleto ?? '—'}
            detalhe={(item) =>
              [item.cpfCnpj ? formatCpfCnpj(item.cpfCnpj) : null, item.email]
                .filter(Boolean)
                .join(' · ') || undefined
            }
            aoEscolher={setSelecionado}
            rotuloAcessivel="Associados encontrados"
            vazio={
              <EmptyState
                icone="pessoa"
                titulo="Nenhum associado com esse nome"
                descricao="Confira a grafia. A busca é pelo nome cadastrado no SAAC."
              />
            }
          />
        )}

        {selecionado && (
          <ResumoRegistro
            linhas={[
              { rotulo: 'Nome', valor: selecionado.nomeCompleto ?? '—' },
              {
                rotulo: 'CPF/CNPJ',
                valor: selecionado.cpfCnpj ? formatCpfCnpj(selecionado.cpfCnpj) : '—',
              },
              { rotulo: 'E-mail', valor: selecionado.email ?? '—' },
              { rotulo: 'Código no SGA', valor: selecionado.codigoAssociado ?? '—' },
              { rotulo: 'Regional', valor: selecionado.regional ?? '—' },
            ]}
            acao={
              <Botao
                variante="fantasma"
                tamanho="sm"
                disabled={enviando}
                onClick={() => setSelecionado(null)}
              >
                Trocar associado
              </Botao>
            }
          />
        )}
      </div>
    </Modal>
  );
};

export default ModalEnvioDeAssociado;
